import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine
} from 'recharts';

const ComprehensiveChart = ({ data, height = 500 }) => {
  const formatCurrency = (value) => {
    if (Math.abs(value) >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (Math.abs(value) >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (Math.abs(value) >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    } else {
      return `₹${value.toFixed(0)}`;
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded-xl shadow-lg border border-gray-200 min-w-[250px]">
          <p className="font-semibold text-gray-900 mb-3 text-lg">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between py-1">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-gray-700 font-medium">{entry.name}</span>
              </div>
              <span className={`font-bold ${entry.value >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {formatCurrency(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const getBarColor = (value) => {
    return value >= 0 ? '#15803d' : '#b91c1c';
  };

  return (
    <div className="chart-container">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Comprehensive P&L Analysis
        </h3>
        <p className="text-gray-600">
          Annual P&L performance with charges overlay. Negative values shown below the axis.
        </p>
      </div>
      
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis 
            dataKey="fy" 
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            className="text-sm fill-gray-600"
          />
          <YAxis 
            tickFormatter={formatCurrency}
            className="text-sm fill-gray-600"
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={0} stroke="#64748b" strokeDasharray="2 2" />
          
          {/* P&L Bars with conditional colors */}
          <Bar dataKey="total_pnl" name="Total P&L" radius={[4, 4, 4, 4]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.total_pnl)} />
            ))}
          </Bar>
          
          {/* Charges Line */}
          <Line 
            type="monotone" 
            dataKey="total_charges" 
            name="Total Charges"
            stroke="#374151" 
            strokeWidth={3}
            dot={{ fill: '#374151', strokeWidth: 2, r: 6 }}
            activeDot={{ r: 8, fill: '#374151' }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ComprehensiveChart;